import type {
    ConfigOwner,
    ConfigTarget,
    SettingsRegistryEntry,
    SettingsSnapshot,
} from "@magic-md/engine-client/config";
import type { SettingsSection } from "../../../store/settings-store";
import { sectionForOwner } from "./builders";
import type { SettingsTextResolver } from "./localization";
import type { SettingGroup } from "./types";

const TARGET_LABELS: Record<ConfigTarget, string> = {
    user: "%editor.settings.target.user%",
    workspace: "%editor.settings.target.workspace%",
    remoteHost: "%editor.settings.target.remoteHost%",
};

export function preferredLayerOrder(): ConfigTarget[] {
    return ["user", "workspace", "remoteHost"];
}

export function preferredTargetOrder(section: SettingsSection): ConfigTarget[] {
    switch (section) {
        case "runtime":
        case "sidecar":
            return ["remoteHost", "workspace", "user"];
        case "plugin":
            return ["user", "workspace", "remoteHost"];
        case "editor":
        default:
            return ["user", "workspace", "remoteHost"];
    }
}

export function findAvailableTargetsForSection(
    section: SettingsSection,
    entries: SettingsRegistryEntry[],
    capabilities: SettingsSnapshot["capabilities"],
): ConfigTarget[] {
    return preferredTargetOrder(section).filter((target) =>
        entries.some((entry) => {
            if (sectionForOwner(entry.owner) !== section) return false;
            if (!entry.targets.includes(target)) return false;
            if (target === "user") {
                return capabilities.availableTargets.includes("user");
            }
            if (!capabilities.availableTargets.includes(target)) {
                return false;
            }
            const ownerCapability = capabilities.owners[entry.owner];
            return (
                ownerCapability?.readableTargets.includes(target) ||
                ownerCapability?.writableTargets.includes(target)
            );
        }),
    );
}

export function findPreferredTargetForSection(
    section: SettingsSection,
    entries: SettingsRegistryEntry[],
    capabilities: SettingsSnapshot["capabilities"],
): ConfigTarget | null {
    return findAvailableTargetsForSection(section, entries, capabilities)[0] ?? null;
}

export function isWritable(
    entry: SettingsRegistryEntry,
    target: ConfigTarget,
    capabilities?: SettingsSnapshot["capabilities"],
    meta?: SettingsSnapshot["meta"]["entries"][string],
): boolean {
    if (entry.secret) return false;
    if (!entry.writableTargets.includes(target)) return false;
    if (meta?.readOnly) return false;
    const ownerCapability = capabilities?.owners[entry.owner];
    if (!ownerCapability) {
        return target === "user" && entry.owner === "editor";
    }
    return ownerCapability.writableTargets.includes(target);
}

export function normalizeInputValue(
    entry: SettingsRegistryEntry,
    rawValue: unknown,
): unknown {
    const rawType = Array.isArray(entry.schema.type)
        ? entry.schema.type[0]
        : entry.schema.type;
    if (rawType === "integer") {
        const parsed = Number(rawValue);
        return Number.isInteger(parsed) ? parsed : (entry.defaultValue ?? rawValue);
    }
    if (rawType === "number") {
        const parsed = Number(rawValue);
        return Number.isFinite(parsed) ? parsed : (entry.defaultValue ?? rawValue);
    }
    return rawValue;
}

export function getCurrentLayerValue(
    entry: SettingsRegistryEntry,
    target: ConfigTarget,
    snapshot: SettingsSnapshot | null,
    draftValue: unknown,
): unknown {
    const meta = snapshot?.meta.entries[entry.key];
    if (draftValue !== undefined) {
        return draftValue;
    }
    if (meta?.targetValues?.[target] !== undefined) {
        return meta.targetValues[target];
    }
    return entry.defaultValue;
}

export function getExplicitLayerValue(
    entry: SettingsRegistryEntry,
    target: ConfigTarget,
    snapshot: SettingsSnapshot | null,
): unknown {
    return snapshot?.meta.entries[entry.key]?.targetValues?.[target];
}

export function supportedTargets(entry: SettingsRegistryEntry): ConfigTarget[] {
    return preferredLayerOrder().filter((target) => entry.targets.includes(target));
}

export function targetLabel(
    target: ConfigTarget,
    resolveText?: SettingsTextResolver,
): string {
    return resolveSettingsText(TARGET_LABELS[target], target, resolveText);
}

export function overrideMessage(
    target: ConfigTarget,
    meta?: SettingsSnapshot["meta"]["entries"][string],
    resolveText?: SettingsTextResolver,
): string | null {
    if (!meta) return null;
    if (!meta.setInTargets?.[target]) return null;
    if (target === "user" && meta.source === "workspace") {
        return resolveSettingsText(
            "%editor.settings.override.workspace%",
            "Overridden by workspace layer",
            resolveText,
        );
    }
    if (target === "user" && meta.source === "remoteHost") {
        return resolveSettingsText(
            "%editor.settings.override.remoteHost%",
            "Overridden by host layer",
            resolveText,
        );
    }
    if (target === "workspace" && meta.source === "remoteHost") {
        return resolveSettingsText(
            "%editor.settings.override.remoteHost%",
            "Overridden by host layer",
            resolveText,
        );
    }
    return null;
}

export function effectActionLabel(
    entry: SettingsRegistryEntry,
    resolveText?: SettingsTextResolver,
): string {
    if (entry.owner === "sidecar") {
        if (entry.key.endsWith(".enabled")) {
            return resolveSettingsText(
                "%editor.settings.effectAction.updateSidecar%",
                "Update Sidecar state now",
                resolveText,
            );
        }
        return resolveSettingsText(
            "%editor.settings.effectAction.restartSidecar%",
            "Restart Sidecar now",
            resolveText,
        );
    }
    if (entry.owner === "engine" || entry.owner === "gateway") {
        return resolveSettingsText(
            "%editor.settings.effectAction.restartEngine%",
            "Restart Engine now",
            resolveText,
        );
    }
    return resolveSettingsText(
        "%editor.settings.effectAction.apply%",
        "Apply now",
        resolveText,
    );
}

export function resolveContextOwners({
    activeSection,
    sectionEntries,
    selectedEntry,
    selectedPluginGroup,
    selectedSidecarGroup,
}: {
    activeSection: SettingsSection;
    sectionEntries: SettingsRegistryEntry[];
    selectedEntry: SettingsRegistryEntry | null;
    selectedPluginGroup: SettingGroup | null;
    selectedSidecarGroup: SettingGroup | null;
}): ConfigOwner[] {
    if (selectedEntry) {
        return [selectedEntry.owner];
    }
    if (selectedPluginGroup) {
        return ["plugin"];
    }
    if (selectedSidecarGroup) {
        return ["sidecar"];
    }
    if (activeSection === "plugin") {
        return ["plugin"];
    }
    if (activeSection === "sidecar") {
        return ["sidecar"];
    }
    const owners = [...new Set(sectionEntries.map((entry) => entry.owner))];
    if (owners.length > 0) {
        return owners;
    }
    return activeSection === "runtime" ? ["engine"] : ["editor"];
}

export function layerContextLabel(
    target: ConfigTarget,
    owners: ConfigOwner[],
    resolveText?: SettingsTextResolver,
): string {
    return `${layerConsumerLabel(target, owners, resolveText)} · ${layerScopeLabel(target, resolveText)}`;
}

function layerConsumerLabel(
    target: ConfigTarget,
    owners: ConfigOwner[],
    resolveText?: SettingsTextResolver,
): string {
    if (target === "user") {
        return owners.includes("extension")
            ? resolveSettingsText(
                "%editor.settings.layer.consumer.extension%",
                "Extension",
                resolveText,
            )
            : owners.includes("plugin")
            ? resolveSettingsText(
                "%editor.settings.layer.consumer.pluginExecution%",
                "Plugin execution",
                resolveText,
            )
            : resolveSettingsText(
                "%editor.settings.layer.consumer.editor%",
                "Editor",
                resolveText,
            );
    }
    if (target === "workspace") {
        if (owners.includes("extension")) {
            return resolveSettingsText(
                "%editor.settings.layer.consumer.extension%",
                "Extension",
                resolveText,
            );
        }
        return resolveSettingsText(
            "%editor.settings.layer.consumer.workspaceSession%",
            "Workspace session",
            resolveText,
        );
    }
    if (owners.includes("extension")) {
        return resolveSettingsText(
            "%editor.settings.layer.consumer.extension%",
            "Extension",
            resolveText,
        );
    }
    if (owners.includes("gateway") && owners.some((owner) => owner !== "gateway")) {
        return "Engine / Gateway";
    }
    if (owners.includes("gateway")) {
        return "Gateway";
    }
    return "Engine";
}

function layerScopeLabel(
    target: ConfigTarget,
    resolveText?: SettingsTextResolver,
): string {
    if (target === "user") {
        return resolveSettingsText(
            "%editor.settings.layer.scope.user%",
            "Current user",
            resolveText,
        );
    }
    if (target === "workspace") {
        return resolveSettingsText(
            "%editor.settings.layer.scope.workspace%",
            "Current workspace",
            resolveText,
        );
    }
    return resolveSettingsText(
        "%editor.settings.layer.scope.remoteHost%",
        "Current host",
        resolveText,
    );
}

function resolveSettingsText(
    value: string,
    fallback: string,
    resolveText?: SettingsTextResolver,
): string {
    return resolveText ? resolveText(value, fallback) : fallback;
}

export function collectLayerPaths(
    snapshot: SettingsSnapshot | null,
    target: ConfigTarget,
    owners: ConfigOwner[],
): Array<{ label: string; path: string }> {
    if (!snapshot) return [];

    const paths = new Map<string, { label: string; path: string }>();
    for (const owner of owners) {
        const ownerPaths = snapshot.ownerFilePaths?.[owner];
        const path =
            target === "user"
                ? ownerPaths?.user ?? snapshot.filePaths?.user ?? null
                : target === "workspace"
                    ? ownerPaths?.workspace ?? snapshot.filePaths?.workspace ?? null
                    : ownerPaths?.remoteHost ?? snapshot.filePaths?.remoteHost ?? null;
        if (!path) continue;
        paths.set(path, {
            label: fileLabelForOwnerTarget(owner, target),
            path,
        });
    }
    return [...paths.values()];
}

function fileLabelForOwnerTarget(
    owner: ConfigOwner,
    target: ConfigTarget,
): string {
    if (target === "workspace") {
        return "settings.toml";
    }
    if (target === "user") {
        return "editor.toml";
    }
    if (owner === "gateway") {
        return "gateway.toml";
    }
    return "engine.toml";
}

export async function waitForEngineReady(
    engine: { getEngineStatus: () => Promise<{ alive: boolean; ready: boolean }> },
): Promise<boolean> {
    const initialStatus = await engine.getEngineStatus().catch(() => null);
    if (!initialStatus?.alive) {
        return false;
    }
    if (initialStatus.ready) {
        return true;
    }

    const deadline = Date.now() + 8_000;
    while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 250));
        const status = await engine.getEngineStatus().catch(() => null);
        if (!status?.alive) {
            return false;
        }
        if (status.ready) {
            return true;
        }
    }
    return false;
}
